import { useState } from "react";
import {
  PanelShell, Badge, Button, EmptyState, tokens,
  CUE_SAFE_LANGUAGE,
  type DJSEMStemTypeUI,
  stemTriggerSourceOptions, VISUAL_MAPPING_TARGETS, LIGHTING_CUE_SIM_TARGETS, mappingTargetLabel,
  createStemTriggerMapping, evaluateStemTriggerMapping, isLightingCueTarget,
  type StemTriggerMapping, type StemRuntimeSample, type VisualMappingTarget, type LightingCueSimTarget,
} from "../index";

// Demo approved stems + preview moments for the mapping editor below (Prompt 138). No real
// per-set stem/runtime data is wired to Visuals yet — same "real computation, demo data"
// pattern as SetGraphPanel/StemLanesPanel (Phase 7). The math (evaluateStemTriggerMapping) is
// real and tested; only the underlying sample values are illustrative.
const VISUAL_DEMO_STEM_TYPES: DJSEMStemTypeUI[] = ["vocals", "drums", "bass"];
const VISUAL_PREVIEW_MOMENTS: Array<{ label: string; samples: StemRuntimeSample[] }> = [
  { label: "Intro (quiet)", samples: [
    { stemType: "vocals", energy: 0.1, onsetActive: false, available: true },
    { stemType: "drums", energy: 0.05, onsetActive: false, available: true },
    { stemType: "bass", energy: 0.1, onsetActive: false, available: true },
  ] },
  { label: "Drop (vocal + drum hit)", samples: [
    { stemType: "vocals", energy: 0.9, onsetActive: true, available: true },
    { stemType: "drums", energy: 0.85, onsetActive: true, available: true },
    { stemType: "bass", energy: 0.7, onsetActive: false, available: true },
  ] },
  { label: "Breakdown (bass only)", samples: [
    { stemType: "vocals", energy: 0.05, onsetActive: false, available: true },
    { stemType: "drums", energy: 0.1, onsetActive: false, available: true },
    { stemType: "bass", energy: 0.6, onsetActive: true, available: true },
  ] },
];

/**
 * Visual & Lighting/Cue stem-trigger mapping editor (Prompt 138). Simulation/dry-run only —
 * no real hardware output path exists anywhere in this module. Mappings live in component
 * state (no persistence route exists yet for this feature; not fabricated here).
 *
 * @returns The stem-trigger mapping editor panel, a preview panel evaluating mappings against
 *   sample moments, and a lighting/cue safety panel.
 */
export function VisualsModule() {
  const [triggerIdx, setTriggerIdx] = useState(0);
  const [targetKind, setTargetKind] = useState<"visual" | "lighting_cue">("visual");
  const [target, setTarget] = useState<VisualMappingTarget | LightingCueSimTarget>(VISUAL_MAPPING_TARGETS[0]);
  const [threshold, setThreshold] = useState(0.5);
  const [momentIdx, setMomentIdx] = useState(0);
  const [mappings, setMappings] = useState<StemTriggerMapping[]>([]);

  const triggerOptions = stemTriggerSourceOptions(VISUAL_DEMO_STEM_TYPES);
  const chosenTrigger = triggerOptions[triggerIdx] ?? triggerOptions[0];
  const targetOptions = targetKind === "visual" ? VISUAL_MAPPING_TARGETS : LIGHTING_CUE_SIM_TARGETS;
  const moment = VISUAL_PREVIEW_MOMENTS[momentIdx];

  const addMapping = () => {
    const m = createStemTriggerMapping({
      id: `mapping_${mappings.length + 1}`, stemType: chosenTrigger.stemType, triggerKind: chosenTrigger.kind,
      threshold, targetKind, target,
    });
    setMappings([...mappings, m]);
  };

  return (
    <>
      <PanelShell title="Stem trigger mapping — visual & lighting/cue (simulation)">
        <p style={{ margin: "0 0 10px", color: tokens.color.textDim, fontSize: 13 }}>
          {CUE_SAFE_LANGUAGE.heading} — {CUE_SAFE_LANGUAGE.disclaimer}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 480 }}>
          <div>
            <div style={{ fontSize: 12, color: tokens.color.text, marginBottom: 4 }}>Trigger source</div>
            <select value={triggerIdx} onChange={(e: { target: { value: string } }) => setTriggerIdx(Number(e.target.value))}
              style={{ width: "100%", padding: 6, fontSize: 12, background: tokens.color.bg, color: tokens.color.text, border: `1px solid ${tokens.color.border}`, borderRadius: 4 }}>
              {triggerOptions.map((o, i) => <option key={`${o.stemType}-${o.kind}`} value={i}>{o.label}</option>)}
            </select>
          </div>
          {chosenTrigger?.kind === "energy_threshold" ? (
            <div>
              <div style={{ fontSize: 12, color: tokens.color.text, marginBottom: 4 }}>Threshold: {threshold.toFixed(2)}</div>
              <input type="range" min={0} max={1} step={0.05} value={threshold} onChange={(e: { target: { value: string } }) => setThreshold(Number(e.target.value))} style={{ width: "100%" }} />
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8 }}>
            {(["visual", "lighting_cue"] as const).map((k) => (
              <button key={k} onClick={() => { setTargetKind(k); setTarget(k === "visual" ? VISUAL_MAPPING_TARGETS[0] : LIGHTING_CUE_SIM_TARGETS[0]); }} style={{
                fontSize: 11, padding: "4px 10px", borderRadius: 4, cursor: "pointer",
                background: targetKind === k ? tokens.color.accent : "transparent",
                color: targetKind === k ? "#12100a" : tokens.color.muted,
                border: `1px solid ${targetKind === k ? tokens.color.accentDim : tokens.color.border}`,
              }}>{k === "visual" ? "Visual" : "Lighting/cue (simulation)"}</button>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 12, color: tokens.color.text, marginBottom: 4 }}>Maps to</div>
            <select value={target} onChange={(e: { target: { value: string } }) => setTarget(e.target.value as VisualMappingTarget | LightingCueSimTarget)}
              style={{ width: "100%", padding: 6, fontSize: 12, background: tokens.color.bg, color: tokens.color.text, border: `1px solid ${tokens.color.border}`, borderRadius: 4 }}>
              {targetOptions.map((o) => <option key={o} value={o}>{mappingTargetLabel(o)}</option>)}
            </select>
          </div>
          <Button onClick={addMapping}>Create mapping</Button>
        </div>
      </PanelShell>

      <PanelShell title="Preview" style={{ marginTop: 12 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {VISUAL_PREVIEW_MOMENTS.map((m, i) => (
            <button key={m.label} onClick={() => setMomentIdx(i)} style={{
              fontSize: 11, padding: "4px 10px", borderRadius: 4, cursor: "pointer",
              background: momentIdx === i ? tokens.color.accent : tokens.color.surface2,
              color: momentIdx === i ? "#12100a" : tokens.color.text,
              border: `1px solid ${momentIdx === i ? tokens.color.accentDim : tokens.color.border}`,
            }}>{m.label}</button>
          ))}
        </div>
        {mappings.length === 0 ? (
          <EmptyState title="No mappings yet" detail="Create a mapping above to see it evaluated against the preview moments." />
        ) : mappings.map((m) => {
          const ev = evaluateStemTriggerMapping(m, moment.samples);
          return (
            <div key={m.id} style={{ padding: "6px 0", borderBottom: `1px solid ${tokens.color.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12 }}>
                  {m.stemType} {m.triggerKind === "onset" ? "onset" : `energy ≥ ${m.threshold.toFixed(2)}`} → {mappingTargetLabel(m.target)}
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  {isLightingCueTarget(m.targetKind) ? <Badge tone="accent">simulation only</Badge> : null}
                  <Badge tone={ev.fired ? "ok" : "neutral"}>{ev.fired ? "fired" : "idle"}</Badge>
                  <button onClick={() => setMappings(mappings.filter((x) => x.id !== m.id))} style={{ fontSize: 11, background: "none", border: "none", color: tokens.color.muted, cursor: "pointer" }}>remove</button>
                </div>
              </div>
              {ev.warnings.length > 0 ? <div style={{ fontSize: 11, color: "#e8b24a", marginTop: 2 }}>{ev.warnings.join(" · ")}</div> : null}
            </div>
          );
        })}
      </PanelShell>

      <PanelShell title="Lighting & cue safety" style={{ marginTop: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Badge tone="accent">{CUE_SAFE_LANGUAGE.strobeWarning}</Badge>
          <Badge tone="accent">{CUE_SAFE_LANGUAGE.blackoutWarning}</Badge>
        </div>
        <p style={{ margin: "10px 0 0", color: tokens.color.textDim, fontSize: 12 }}>
          Show-pack manifest export for these mappings is not yet wired — no show-pack integration
          exists for stem triggers in this build.
        </p>
      </PanelShell>
    </>
  );
}
